import { useEffect, useState } from "react";

export interface ArrayBackedCollection<T> {
  readonly toArray: T[];
  toArrayWhenReady(): Promise<T[]>;
  subscribeChanges(callback: () => void): { unsubscribe: () => void };
}

const IDLE: ArrayBackedCollection<never> = {
  toArray: [],
  toArrayWhenReady: async () => [],
  subscribeChanges: () => ({ unsubscribe() {} }),
};

export function idleCollection<T>(): ArrayBackedCollection<T> {
  return IDLE as ArrayBackedCollection<T>;
}

function errorMessage(cause: Error | { message?: string } | string | undefined): string {
  if (!cause) return "That list could not be loaded.";
  if (typeof cause === "string") return cause;
  if (typeof cause.message === "string" && cause.message.length > 0) return cause.message;
  return "That list could not be loaded.";
}

function collectionRefetch(collection: object): ((opts?: { throwOnError?: boolean }) => PromiseLike<void>) | undefined {
  if (!("utils" in collection)) return undefined;
  if (typeof collection.utils !== "object" || collection.utils === null) return undefined;
  if (!("refetch" in collection.utils) || typeof collection.utils.refetch !== "function") return undefined;
  const refetch = collection.utils.refetch;
  return (opts?: { throwOnError?: boolean }) => Promise.resolve(refetch(opts)).then(() => undefined);
}

function collectionStatus(collection: object): "idle" | "loading" | "ready" | "error" | "cleaned-up" | undefined {
  if (!("status" in collection)) return undefined;
  if (collection.status === "idle") return "idle";
  if (collection.status === "loading") return "loading";
  if (collection.status === "ready") return "ready";
  if (collection.status === "error") return "error";
  if (collection.status === "cleaned-up") return "cleaned-up";
  return undefined;
}

function errorFromCollection(collection: object): string | null {
  if (!("utils" in collection)) return null;
  if (typeof collection.utils !== "object" || collection.utils === null) return null;
  if (!("isError" in collection.utils) || collection.utils.isError !== true) return null;
  if (!("lastError" in collection.utils)) return errorMessage(undefined);
  if (collection.utils.lastError instanceof Error) return errorMessage(collection.utils.lastError);
  if (
    typeof collection.utils.lastError === "object" &&
    collection.utils.lastError !== null &&
    "message" in collection.utils.lastError &&
    typeof collection.utils.lastError.message === "string"
  ) {
    return errorMessage(collection.utils.lastError.message);
  }
  return errorMessage(undefined);
}

function initialLoading<T>(collection: ArrayBackedCollection<T>): boolean {
  if (collection.toArray.length > 0) return false;
  if (!collectionRefetch(collection)) return false;
  const status = collectionStatus(collection);
  if (status === "ready" || status === "error") return false;
  return true;
}

export function useCollectionArray<T>(
  collection: ArrayBackedCollection<T>,
): { rows: T[]; loading: boolean; error: string | null } {
  const [rows, setRows] = useState<T[]>(() => collection.toArray);
  const [loading, setLoading] = useState(() => initialLoading(collection));
  const [error, setError] = useState<string | null>(() => errorFromCollection(collection));

  useEffect(() => {
    let cancelled = false;
    let settled = !initialLoading(collection);
    setRows(collection.toArray);
    setLoading(!settled);
    setError(errorFromCollection(collection));

    const subscription = collection.subscribeChanges(() => {
      if (cancelled) return;
      setRows(collection.toArray);
      if (!settled) return;
      const liveError = errorFromCollection(collection);
      if (liveError) {
        setError(liveError);
        return;
      }
      setError(null);
    });

    const refetch = collectionRefetch(collection);
    if (refetch) {
      void Promise.resolve(refetch({ throwOnError: true })).then(
        () => {
          if (cancelled) return;
          settled = true;
          setRows(collection.toArray);
          setLoading(false);
          setError(errorFromCollection(collection));
        },
        (cause: Error | string) => {
          if (cancelled) return;
          settled = true;
          setLoading(false);
          setError(errorFromCollection(collection) ?? errorMessage(cause));
        },
      );
    } else {
      settled = true;
      setLoading(false);
      setError(null);
    }

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [collection]);

  return { rows, loading, error };
}
